import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, Image as ImageIcon, X } from 'lucide-react'
import type { BlockProps } from '../types.js'
import { SectionWrapper } from './SectionWrapper.js'
import { EMPTY_ARRAY } from './formatters.js'
import common from './common.module.css'
import s from './GalleryBlock.module.css'

type GalleryLayout = 'grid-2' | 'grid-3' | 'grid-2-1' | 'single-column' | 'masonry' | 'carousel' | 'filmstrip' | 'highlight'

const SUPPORTED_LAYOUTS: GalleryLayout[] = ['grid-2', 'grid-3', 'grid-2-1', 'single-column', 'masonry', 'carousel', 'filmstrip', 'highlight']

interface GalleryBlockProps extends BlockProps {
  layout?: GalleryLayout
  css: Record<string, string>
}

export function GalleryBlock({ invitation, activeSection, onSectionClick, mode, layout: layoutProp = 'grid-2', css }: GalleryBlockProps) {
  const galleryImages = invitation.gallery?.images ?? EMPTY_ARRAY
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  // Settings override > prop > default
  const rawLayout = (invitation.settings?.galleryLayout as GalleryLayout) ?? layoutProp
  // Unsupported layouts fall back to grid-2
  const layout: GalleryLayout = SUPPORTED_LAYOUTS.includes(rawLayout) ? rawLayout : 'grid-2'

  // Aspect ratio — CSS 변수로 전달 (적용 대상: grid-2, grid-3, single-column, masonry)
  const aspectRatio = invitation.settings?.galleryAspectRatio ?? null
  const aspectStyle = aspectRatio ? { '--gallery-ratio': aspectRatio.replace(':', ' / ') } as React.CSSProperties : undefined

  const renderImage = (url: string, index: number) => (
    <img
      key={index}
      src={url}
      alt={`갤러리 ${index + 1}`}
      className={s.galleryImage}
      onClick={(e) => { e.stopPropagation(); setLightboxIndex(index) }}
      style={{ cursor: 'pointer' }}
    />
  )

  const renderEmpty = (index: number, iconSize: number = 14) => (
    <div key={index} className={s.emptySlot}>
      <ImageIcon size={iconSize} />
    </div>
  )

  const renderAll = (images: string[], emptyCount: number, iconSize: number = 14) => {
    if (images.length > 0) {
      return images.map((url, i) => (
        <div key={i}>{renderImage(url, i)}</div>
      ))
    }
    return Array.from({ length: emptyCount }).map((_, i) => (
      <div key={i}>{renderEmpty(i, iconSize)}</div>
    ))
  }

  const renderLayout = () => {
    switch (layout) {
      case 'grid-2':
        return (
          <div className={s.grid2} style={aspectStyle}>
            {renderAll(galleryImages, 4)}
          </div>
        )

      case 'grid-3':
        return (
          <div className={s.grid3} style={aspectStyle}>
            {renderAll(galleryImages, 6)}
          </div>
        )

      case 'grid-2-1': {
        if (galleryImages.length === 0) {
          return (
            <div className={s.grid21}>
              <div className={s.grid21Row}>{renderEmpty(0)}{renderEmpty(1)}</div>
              <div className={s.grid21Full}>{renderEmpty(2, 16)}</div>
            </div>
          )
        }
        const rows: React.ReactNode[] = []
        for (let i = 0; i < galleryImages.length; i += 3) {
          const chunk = galleryImages.slice(i, i + 3)
          if (chunk.length >= 2) {
            rows.push(
              <div key={`r${i}`} className={s.grid21Row}>
                {renderImage(chunk[0], i)}
                {renderImage(chunk[1], i + 1)}
              </div>
            )
          }
          if (chunk.length === 3) {
            rows.push(
              <div key={`f${i}`} className={s.grid21Full}>
                {renderImage(chunk[2], i + 2)}
              </div>
            )
          } else if (chunk.length === 1) {
            rows.push(
              <div key={`f${i}`} className={s.grid21Full}>
                {renderImage(chunk[0], i)}
              </div>
            )
          }
        }
        return <div className={s.grid21}>{rows}</div>
      }

      case 'single-column':
        return (
          <div className={s.singleColumn} style={aspectStyle}>
            {renderAll(galleryImages, 3, 16)}
          </div>
        )

      case 'masonry':
        return (
          <div className={s.masonry} style={aspectStyle}>
            {galleryImages.length > 0
              ? galleryImages.map((url, i) => (
                  <div key={i} className={i % 3 === 0 ? s.masonryTall : undefined}>
                    {renderImage(url, i)}
                  </div>
                ))
              : Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className={i % 3 === 0 ? s.masonryTall : undefined}>
                    {renderEmpty(i)}
                  </div>
                ))
            }
          </div>
        )

      case 'filmstrip':
        return (
          <div className={s.filmstrip}>
            {renderAll(galleryImages, 4)}
          </div>
        )

      case 'carousel': {
        const hasTemplateCss = css.galleryScroll && css.gallerySlide
        if (hasTemplateCss) {
          return (
            <div className={css.galleryScroll}>
              {galleryImages.length > 0
                ? galleryImages.map((url, i) => (
                    <div key={i} className={css.gallerySlide}>
                      <img src={url} alt={`갤러리 ${i + 1}`} className={s.galleryImage} />
                    </div>
                  ))
                : Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className={`${css.gallerySlide} ${css.galleryEmpty ?? ''}`}>
                      <ImageIcon size={16} />
                    </div>
                  ))
              }
            </div>
          )
        }
        return (
          <div className={s.carousel}>
            {galleryImages.length > 0
              ? galleryImages.map((url, i) => (
                  <div key={i}>{renderImage(url, i)}</div>
                ))
              : Array.from({ length: 3 }).map((_, i) => (
                  <div key={i}>{renderEmpty(i, 16)}</div>
                ))
            }
          </div>
        )
      }

      case 'highlight': {
        const hasImages = galleryImages.length > 0
        return (
          <div className={s.highlight}>
            <div className={s.highlightMain}>
              {hasImages
                ? renderImage(galleryImages[0], 0)
                : renderEmpty(0, 16)
              }
            </div>
            <div className={s.highlightGrid}>
              {hasImages
                ? galleryImages.slice(1).map((url, i) => (
                    <div key={i + 1}>{renderImage(url, i + 1)}</div>
                  ))
                : Array.from({ length: 3 }).map((_, i) => (
                    <div key={i + 1}>{renderEmpty(i + 1)}</div>
                  ))
              }
            </div>
          </div>
        )
      }

      default:
        return (
          <div className={s.grid2}>
            {renderAll(galleryImages, 4)}
          </div>
        )
    }
  }

  return (
    <SectionWrapper
      sectionId="gallery"
      activeSection={activeSection}
      onSectionClick={onSectionClick}
      mode={mode}
      className={common.section}
    >
      <span className={common.sectionTitle}>갤러리</span>
      <div className={common.divider} />
      {renderLayout()}
      {lightboxIndex !== null && (
        <GalleryLightbox
          images={galleryImages}
          index={lightboxIndex}
          onChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </SectionWrapper>
  )
}

/* ── GalleryLightbox ── */

function GalleryLightbox({ images, index, onChange, onClose }: {
  images: string[]
  index: number
  onChange: (i: number) => void
  onClose: () => void
}) {
  const goPrev = useCallback(() => {
    onChange((index - 1 + images.length) % images.length)
  }, [index, images.length, onChange])

  const goNext = useCallback(() => {
    onChange((index + 1) % images.length)
  }, [index, images.length, onChange])

  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft') goPrev()
      else if (e.key === 'ArrowRight') goNext()
    }
    document.addEventListener('keydown', handle)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handle)
      document.body.style.overflow = ''
    }
  }, [goPrev, goNext, onClose])

  return createPortal(
    <div className={s.lightbox} onClick={onClose}>
      <button className={s.lightboxClose} onClick={onClose} aria-label="닫기">
        <X size={24} />
      </button>
      {images.length > 1 && (
        <button className={`${s.lightboxNav} ${s.lightboxPrev}`} onClick={(e) => { e.stopPropagation(); goPrev() }} aria-label="이전">
          <ChevronLeft size={28} />
        </button>
      )}
      <img
        className={s.lightboxImage}
        src={images[index]}
        alt={`갤러리 ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
      />
      {images.length > 1 && (
        <button className={`${s.lightboxNav} ${s.lightboxNext}`} onClick={(e) => { e.stopPropagation(); goNext() }} aria-label="다음">
          <ChevronRight size={28} />
        </button>
      )}
      <div className={s.lightboxCounter}>{index + 1} / {images.length}</div>
    </div>,
    document.body,
  )
}
